import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { uploadToStorage } from './storageService';

const COLORS = {
  primary: [220, 38, 38],
  secondary: [107, 114, 128],
  text: [17, 24, 39],
  border: [229, 231, 235],
  background: [249, 250, 251]
};

/**
 * Generate comprehensive PDF report with cover page, TOC, embedded images, and watermarks
 */
export async function generateReportPDF(reportData, caseMetadata, images = []) {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter'
    });

    let currentPage = 1;
    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const pageHeight = doc?.internal?.pageSize?.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    // Watermark function
    const addWatermark = () => {
      doc?.setTextColor(200, 200, 200);
      doc?.setFontSize(8);
      doc?.text('AI-GENERATED PRELIMINARY REPORT — See full disclosure in report. Not professional advice. Not an expert report.', pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc?.setTextColor(...COLORS?.text);
    };

    // Page number function
    const addPageNumber = () => {
      doc?.setFontSize(9);
      doc?.setTextColor(...COLORS?.secondary);
      doc?.text(`Page ${currentPage}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      doc?.setTextColor(...COLORS?.text);
      currentPage++;
    };

    // New page function
    const addNewPage = () => {
      doc?.addPage();
      addWatermark();
      addPageNumber();
    };

    // === COVER PAGE ===
    doc?.setFillColor(...COLORS?.primary);
    doc?.rect(0, 0, pageWidth, 70, 'F');
    
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(32);
    doc?.setFont('helvetica', 'bold');
    doc?.text('FIRE FORENSICS REPORT', pageWidth / 2, 35, { align: 'center' });
    
    doc?.setFontSize(14);
    doc?.text('Preliminary AI-Assisted Analysis', pageWidth / 2, 50, { align: 'center' });

    // FireCaseIQ Branding
    doc?.setTextColor(...COLORS?.text);
    doc?.setFontSize(18);
    doc?.setFont('helvetica', 'bold');
    doc?.text('FireCaseIQ', pageWidth / 2, 90, { align: 'center' });
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...COLORS?.secondary);
    doc?.text('Advanced Fire Investigation Intelligence', pageWidth / 2, 98, { align: 'center' });

    // Case metadata
    doc?.setTextColor(...COLORS?.text);
    doc?.setFontSize(14);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`Case #${caseMetadata?.caseNumber}`, pageWidth / 2, 120, { align: 'center' });

    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    doc?.text(`Incident Date: ${caseMetadata?.incidentDate}`, pageWidth / 2, 135, { align: 'center' });
    doc?.text(`Location: ${caseMetadata?.location}`, pageWidth / 2, 145, { align: 'center' });
    doc?.text(`Report Generated: ${caseMetadata?.generatedDate}`, pageWidth / 2, 155, { align: 'center' });

    // Disclaimer box
    doc?.setFillColor(...COLORS?.background);
    doc?.rect(margin, 175, contentWidth, 65, 'F');
    doc?.setDrawColor(...COLORS?.border);
    doc?.rect(margin, 175, contentWidth, 65, 'S');

    doc?.setFontSize(10);
    doc?.setTextColor(...COLORS?.secondary);
    doc?.setFont('helvetica', 'bold');
    doc?.text('AI-GENERATED PRELIMINARY REPORT — DISCLOSURE & LIMITATIONS', pageWidth / 2, 182, { align: 'center' });
    
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'normal');
    const disclaimerText = 'This report was generated by FireCaseIQ (operated by Filmology LLC d/b/a Grunt Logic) using AI (including GPT-based models via OpenAI). It is a preliminary, informational assessment based solely on materials submitted by the user. This report is NOT legal advice, NOT an engineering opinion, NOT a licensed on-site fire investigation, NOT an insurance coverage determination, and NOT an affidavit or retained expert report. It does not create any professional relationship. AI systems can produce inaccurate or incomplete outputs. Do not make legal, insurance, financial, or safety decisions solely based on this report. Consult qualified licensed professionals for definitive determinations.';
    const disclaimerLines = doc?.splitTextToSize(disclaimerText, contentWidth - 10);
    doc?.text(disclaimerLines, pageWidth / 2, 190, { align: 'center', maxWidth: contentWidth - 10 });

    doc?.setFontSize(7);
    doc?.text('Contact: info@firecaseiq.com | 5448 Apex Peakway Suite 157, Apex NC 27502', pageWidth / 2, 232, { align: 'center' });

    addWatermark();
    addPageNumber();

    // === AI NOTICE PAGE ===
    addNewPage();
    let noticeY = 25;

    // Notice header bar
    doc?.setFillColor(254, 243, 199); // amber-100
    doc?.rect(margin, noticeY, contentWidth, 12, 'F');
    doc?.setDrawColor(251, 191, 36); // amber-400
    doc?.rect(margin, noticeY, contentWidth, 12, 'S');
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(120, 53, 15); // amber-900
    doc?.text('AI-GENERATED PRELIMINARY REPORT NOTICE', pageWidth / 2, noticeY + 8, { align: 'center' });
    noticeY += 18;

    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...COLORS?.text);
    doc?.text('Important Notice – AI-Generated Analysis', margin, noticeY);
    noticeY += 8;

    const noticeBlocks = [
      [
        'This report was generated using artificial intelligence through the FireCaseIQ platform. It is provided solely as a preliminary analytical tool based on materials submitted by the user (such as photographs, documents, notes, and metadata).',
        'This report is not a professional fire investigation opinion and should not be treated as a definitive determination of fire origin, cause, liability, or coverage.',
      ],
    ];

    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...COLORS?.text);
    for (const para of noticeBlocks[0]) {
      const lines = doc?.splitTextToSize(para, contentWidth);
      lines.forEach(line => { doc?.text(line, margin, noticeY); noticeY += 5.5; });
      noticeY += 3;
    }

    doc?.text('This report:', margin, noticeY); noticeY += 6;
    const noticeBullets = [
      'is not legal advice',
      'is not an engineering opinion',
      'is not a licensed fire investigation report',
      'is not an expert witness report',
      'does not establish chain-of-custody for evidence',
      'may contain errors, omissions, or incorrect interpretations',
    ];
    for (const b of noticeBullets) {
      doc?.text(`• ${b}`, margin + 4, noticeY); noticeY += 5.5;
    }
    noticeY += 3;

    const noticePara2 = 'The system does not perform site inspections, laboratory testing, electrical measurements, or physical evidence collection. All analysis is limited to the information provided to the platform.';
    const noticePara3 = 'Artificial intelligence systems can generate outputs that are incomplete, inaccurate, or misleading, and any conclusions, probability estimates, or confidence scores should be treated as preliminary observations only.';
    for (const para of [noticePara2, noticePara3]) {
      const lines = doc?.splitTextToSize(para, contentWidth);
      lines.forEach(line => { doc?.text(line, margin, noticeY); noticeY += 5.5; });
      noticeY += 3;
    }

    doc?.setFont('helvetica', 'bold');
    doc?.text('Not for Legal or Safety Decisions', margin, noticeY); noticeY += 6;
    doc?.setFont('helvetica', 'normal');
    const noticeParaLegal = 'This report should not be used as the sole basis for legal, insurance, investigative, financial, or safety decisions. Users should consult qualified licensed professionals and independently verify all findings before relying on this report in any professional or legal context.';
    doc?.splitTextToSize(noticeParaLegal, contentWidth).forEach(line => { doc?.text(line, margin, noticeY); noticeY += 5.5; });
    noticeY += 3;

    doc?.setFont('helvetica', 'bold');
    doc?.text('Evidence Handling', margin, noticeY); noticeY += 6;
    doc?.setFont('helvetica', 'normal');
    const noticeParaEvidence = 'FireCaseIQ does not provide evidence preservation or forensic chain-of-custody guarantees. Original evidence and investigative records should be preserved independently in accordance with applicable investigative procedures.';
    doc?.splitTextToSize(noticeParaEvidence, contentWidth).forEach(line => { doc?.text(line, margin, noticeY); noticeY += 5.5; });
    noticeY += 3;

    doc?.setFont('helvetica', 'bold');
    doc?.text('Full Policy and Limitations', margin, noticeY); noticeY += 6;
    doc?.setFont('helvetica', 'normal');
    const noticeParaPolicy = 'For a complete explanation of the limitations, responsibilities, and legal terms governing AI-generated reports, please review the full policy: https://www.firecaseiq.com/report-disclaimer';
    doc?.splitTextToSize(noticeParaPolicy, contentWidth).forEach(line => { doc?.text(line, margin, noticeY); noticeY += 5.5; });
    noticeY += 3;

    doc?.setFont('helvetica', 'bold');
    doc?.text('Strong Recommendation', margin, noticeY); noticeY += 6;
    doc?.setFont('helvetica', 'normal');
    const noticeParaRec = 'We strongly recommend that all users read and understand the full policy before using or sharing this report, particularly if it may be referenced in investigative, insurance, or legal contexts.';
    doc?.splitTextToSize(noticeParaRec, contentWidth).forEach(line => { doc?.text(line, margin, noticeY); noticeY += 5.5; });
    noticeY += 5;

    // Acknowledgement box
    doc?.setFillColor(...COLORS?.background);
    const ackText = 'By accessing or using this report, you acknowledge that it is an AI-generated preliminary analysis subject to the limitations described in the policy.';
    const ackLines = doc?.splitTextToSize(ackText, contentWidth - 10);
    const ackHeight = ackLines.length * 5.5 + 10;
    doc?.rect(margin, noticeY, contentWidth, ackHeight, 'F');
    doc?.setDrawColor(...COLORS?.border);
    doc?.rect(margin, noticeY, contentWidth, ackHeight, 'S');
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(...COLORS?.secondary);
    let ackY = noticeY + 7;
    ackLines.forEach(line => { doc?.text(line, margin + 5, ackY); ackY += 5.5; });
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...COLORS?.text);

    // === TABLE OF CONTENTS ===
    addNewPage();
    doc?.setFontSize(20);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(...COLORS?.primary);
    doc?.text('TABLE OF CONTENTS', margin, 30);

    let tocY = 50;
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(...COLORS?.text);

    const tocEntries = [];
    reportData?.sections?.forEach((section, index) => {
      tocEntries?.push({
        number: section?.number,
        title: section?.title,
        page: currentPage + index
      });
    });

    tocEntries?.forEach((entry) => {
      if (tocY > pageHeight - 40) {
        addNewPage();
        tocY = 30;
      }
      
      doc?.setFont('helvetica', 'bold');
      doc?.text(`${entry?.number}.`, margin + 5, tocY);
      doc?.setFont('helvetica', 'normal');
      doc?.text(entry?.title, margin + 15, tocY);
      
      // Dotted line
      const titleWidth = doc?.getTextWidth(`${entry?.number}. ${entry?.title}`);
      const dotsStart = margin + 15 + titleWidth + 5;
      const dotsEnd = pageWidth - margin - 20;
      doc?.setTextColor(...COLORS?.border);
      for (let x = dotsStart; x < dotsEnd; x += 3) {
        doc?.text('.', x, tocY);
      }
      
      doc?.setTextColor(...COLORS?.text);
      doc?.text(`${entry?.page}`, pageWidth - margin - 5, tocY, { align: 'right' });
      tocY += 8;
    });

    // === REPORT SECTIONS ===
    for (const section of reportData?.sections || []) {
      addNewPage();
      let yPos = 30;

      // Section header
      doc?.setFontSize(18);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(...COLORS?.primary);
      doc?.text(`${section?.number}. ${section?.title}`, margin, yPos);
      yPos += 10;

      doc?.setDrawColor(...COLORS?.primary);
      doc?.setLineWidth(0.5);
      doc?.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // Section content
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(...COLORS?.text);

      if (section?.type === 'text') {
        const contentLines = doc?.splitTextToSize(section?.content, contentWidth);
        contentLines?.forEach(line => {
          if (yPos > pageHeight - 40) {
            addNewPage();
            yPos = 30;
          }
          doc?.text(line, margin, yPos);
          yPos += 6;
        });
      } else if (section?.type === 'list') {
        const items = Array.isArray(section?.items) ? section?.items : 
                     (typeof section?.content === 'string' ? section?.content?.split('\n')?.filter(Boolean) : []);
        
        items?.forEach(item => {
          if (yPos > pageHeight - 40) {
            addNewPage();
            yPos = 30;
          }
          const cleanItem = item?.replace(/^[•\-*]\s*/, '');
          const itemLines = doc?.splitTextToSize(`• ${cleanItem}`, contentWidth - 5);
          itemLines?.forEach(line => {
            doc?.text(line, margin + 5, yPos);
            yPos += 6;
          });
          yPos += 2;
        });
      } else if (section?.type === 'table' && section?.data) {
        doc?.autoTable({
          startY: yPos,
          head: [section?.data?.headers || []],
          body: section?.data?.rows || [],
          theme: 'grid',
          headStyles: { fillColor: COLORS?.primary },
          margin: { left: margin, right: margin },
          styles: { fontSize: 9 }
        });
        yPos = doc?.lastAutoTable?.finalY + 10;
      }

      // Embed images in relevant sections
      if (section?.number === '4' || section?.number === '9') {
        yPos += 10;

        // Add OCR Results subsection in Materials Reviewed (Section 4)
        if (section?.number === '4' && caseMetadata?.ocrResults?.length > 0) {
          doc?.setFontSize(12);
          doc?.setFont('helvetica', 'bold');
          doc?.setTextColor(...COLORS?.primary);
          doc?.text('Extracted Handwritten Text (OCR)', margin, yPos);
          yPos += 8;

          doc?.setFontSize(9);
          doc?.setFont('helvetica', 'normal');
          doc?.setTextColor(...COLORS?.text);

          for (const ocr of caseMetadata?.ocrResults) {
            if (yPos > pageHeight - 60) {
              addNewPage();
              yPos = 30;
            }

            // File name
            doc?.setFont('helvetica', 'bold');
            doc?.text(`File: ${ocr?.file_name}`, margin + 5, yPos);
            yPos += 6;

            // Confidence and quality indicators
            const confidencePercent = (ocr?.confidence * 100)?.toFixed(0);
            const confidenceColor = ocr?.confidence >= 0.8 ? [34, 197, 94] : 
                                   ocr?.confidence >= 0.6 ? [251, 191, 36] : 
                                   [239, 68, 68];
            
            doc?.setFont('helvetica', 'normal');
            doc?.text(`Confidence: `, margin + 5, yPos);
            doc?.setTextColor(...confidenceColor);
            doc?.text(`${confidencePercent}%`, margin + 5 + doc?.getTextWidth('Confidence: '), yPos);
            doc?.setTextColor(...COLORS?.text);
            doc?.text(` | Quality: ${ocr?.quality} | Language: ${ocr?.language}`, margin + 5 + doc?.getTextWidth(`Confidence: ${confidencePercent}%`), yPos);
            yPos += 6;

            // Extracted text
            doc?.setFillColor(...COLORS?.background);
            const textHeight = Math.min(doc?.splitTextToSize(ocr?.text, contentWidth - 20)?.length * 5 + 10, 40);
            doc?.rect(margin + 5, yPos, contentWidth - 10, textHeight, 'F');
            doc?.setDrawColor(...COLORS?.border);
            doc?.rect(margin + 5, yPos, contentWidth - 10, textHeight, 'S');
            
            doc?.setFontSize(9);
            doc?.setFont('helvetica', 'italic');
            const textLines = doc?.splitTextToSize(ocr?.text || 'No text extracted', contentWidth - 20);
            let textY = yPos + 5;
            textLines?.slice(0, 6)?.forEach(line => {
              doc?.text(line, margin + 10, textY);
              textY += 5;
            });
            if (textLines?.length > 6) {
              doc?.text('[Text truncated - view full text in online report]', margin + 10, textY);
            }
            yPos += textHeight + 10;

            doc?.setFont('helvetica', 'normal');
            doc?.setTextColor(...COLORS?.text);
          }

          yPos += 10;
        }

        for (const image of images) {
          if (yPos > pageHeight - 100) {
            addNewPage();
            yPos = 30;
          }

          try {
            const imgWidth = contentWidth;
            const imgHeight = 80;
            doc?.addImage(image?.url, 'JPEG', margin, yPos, imgWidth, imgHeight);
            yPos += imgHeight + 5;

            if (image?.caption) {
              doc?.setFontSize(9);
              doc?.setTextColor(...COLORS?.secondary);
              doc?.setFont('helvetica', 'italic');
              const captionLines = doc?.splitTextToSize(`Figure: ${image?.caption}`, contentWidth);
              captionLines?.forEach(line => {
                doc?.text(line, margin, yPos);
                yPos += 5;
              });
              doc?.setFontSize(10);
              doc?.setFont('helvetica', 'normal');
              doc?.setTextColor(...COLORS?.text);
            }
            yPos += 10;
          } catch (imgError) {
            console.error('Image embed error:', imgError);
            doc?.setTextColor(...COLORS?.secondary);
            doc?.text('[Image could not be embedded - view online report]', margin, yPos);
            yPos += 10;
            doc?.setTextColor(...COLORS?.text);
          }
        }
      }
    }

    const pdfBlob = doc?.output('blob');
    return {
      success: true,
      blob: pdfBlob,
      fileName: `FireCaseIQ_Report_${caseMetadata?.caseNumber}_${Date.now()}.pdf`
    };
  } catch (error) {
    console.error('PDF generation error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to generate PDF'
    };
  }
}

/**
 * Upload report PDF to storage
 */
export async function uploadReportPDF(pdfBlob, caseId, userId) {
  try {
    const fileName = `${userId}/${caseId}/report_${Date.now()}.pdf`;
    const uploadResult = await uploadToStorage('report-pdfs', fileName, pdfBlob, {
      contentType: 'application/pdf',
      upsert: false
    });

    if (!uploadResult?.success) {
      throw new Error(uploadResult?.error);
    }

    return {
      success: true,
      path: uploadResult?.data?.path
    };
  } catch (error) {
    console.error('PDF upload error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to upload PDF'
    };
  }
}
